/**
 * Find a starter template by job name using prefix match.
 * Matches if the job name starts with the template name (case-insensitive).
 * Only considers active templates (status === 'Active').
 * When multiple templates match, returns the most specific (longest name) match.
 * e.g., "Clean Pool Pump" matches "Clean Pool" template over "Clean" template.
 */
export function findOnboardingMatchingTemplate<
  T extends { name: string; status: string }
>(jobName: string, templateList: T[] | null | undefined): T | null {
  if (!templateList) return null;

  // Normalize job name to lowercase for case-insensitive comparison
  const normalizedJobName = jobName.toLowerCase();

  // Filter to find all matching templates where:
  // 1. status === Status.Active
  // 2. job name starts with the template name (case-insensitive)
  const matchingTemplates = templateList.filter(
    (t) =>
      t.status === 'Active' &&
      normalizedJobName.startsWith(t.name.toLowerCase())
  );

  if (matchingTemplates.length === 0) {
    return null;
  }

  // Return the template with the longest name (most specific match)
  // e.g., "Clean Pool" is more specific than "Clean" for job name "Clean Pool Pump"
  const bestMatch = matchingTemplates.reduce(
    (longest, current) =>
      current.name.length > longest.name.length ? current : longest,
    matchingTemplates[0]
  );

  return bestMatch;
}
